import type { DistributeFeedItem, FeedBoxMaterial } from '@/api/distribute'
import { normalizeMediaUrl, resolveMaterialUrl } from '@/utils/distribute/media-url'

export type FeedCardKind =
  | 'media-video'
  | 'media-mv'
  | 'media-imgtxt'
  | 'radio'
  | 'pipe-video'
  | 'pipe-image'
  | 'unknown'

export type FeedCardVariant = 'media' | 'pipe'

export interface FeedCardModel {
  kind: FeedCardKind
  label: string
  item: DistributeFeedItem
  primaryMaterial: FeedBoxMaterial | null
  previewImages: FeedBoxMaterial[]
  previewVideoCoverUrl: string | null
  previewVideoUrl: string | null
  previewAudioUrl: string | null
  coverUrl: string | null
  aspectRatio: string
  hasCollection: boolean
  hasAnchors: boolean
}

function isVideoMaterial(material: FeedBoxMaterial | null | undefined): boolean {
  const src =
    normalizeMediaUrl(material?.src)?.toLowerCase() ??
    resolveMaterialUrl(material)?.toLowerCase() ??
    ''

  return (
    material?.type === 'video' ||
    src.endsWith('.mp4') ||
    src.endsWith('.mov') ||
    src.includes('.m3u8')
  )
}

function isImageMaterial(material: FeedBoxMaterial | null | undefined): boolean {
  return Boolean(resolveMaterialUrl(material, { includeCover: true })) && !isVideoMaterial(material)
}

function resolveImageMaterialDisplayUrl(
  material: FeedBoxMaterial | null | undefined,
  kind: FeedCardKind,
): string | null {
  if (kind === 'pipe-image') {
    return normalizeMediaUrl(material?.src) ?? normalizeMediaUrl(material?.cover)
  }

  return resolveMaterialUrl(material, { includeCover: true })
}

function resolveVideoMaterialPlaybackUrl(
  material: FeedBoxMaterial | null | undefined,
  kind: FeedCardKind,
): string | null {
  if (kind === 'pipe-video') {
    return normalizeMediaUrl(material?.src)
  }

  return resolveMaterialUrl(material)
}

function resolveKind(
  item: DistributeFeedItem,
  primaryMaterial: FeedBoxMaterial | null,
): FeedCardKind {
  if (item.obj === 'radio' || item.func === 'radio') {
    return 'radio'
  }

  if (item.obj === 'media') {
    if (item.func === 'imgtxt') {
      return 'media-imgtxt'
    }

    if (item.func === 'mv' && item.bgmLib) {
      return 'media-mv'
    }

    return 'media-video'
  }

  if (item.obj === 'pipe') {
    return isVideoMaterial(primaryMaterial) ? 'pipe-video' : 'pipe-image'
  }

  return 'unknown'
}

function resolveLabel(kind: FeedCardKind): string {
  switch (kind) {
    case 'media-video':
      return '视频'
    case 'media-mv':
      return 'MV'
    case 'media-imgtxt':
      return '图文'
    case 'radio':
      return '听听'
    case 'pipe-video':
      return '互动'
    case 'pipe-image':
      return '故事'
    default:
      return '内容'
  }
}

function resolveAspectRatio(kind: FeedCardKind, material: FeedBoxMaterial | null): string {
  if (kind === 'radio') {
    return '3 / 4'
  }

  if (kind === 'pipe-video' || kind === 'pipe-image') {
    return '10 / 16'
  }

  if (material?.width && material?.height) {
    return `${Math.max(1, Math.round(material.width))} / ${Math.max(1, Math.round(material.height))}`
  }

  switch (kind) {
    case 'media-video':
    case 'media-mv':
      return '16 / 10'
    case 'media-imgtxt':
      return '3 / 4'
    default:
      return '3 / 4'
  }
}

function resolveFirstMediaUrl(...sources: Array<string | null | undefined>): string | null {
  for (const source of sources) {
    const normalized = normalizeMediaUrl(source)

    if (normalized) {
      return normalized
    }
  }

  return null
}

export function resolveFeedCardModel(item: DistributeFeedItem): FeedCardModel {
  const materials = item.materials ?? []
  const primaryMaterial = materials[0] ?? null
  const kind = resolveKind(item, primaryMaterial)
  const previewImages = materials
    .filter((material) => isImageMaterial(material))
    .map((material) => ({
      ...material,
      src: resolveImageMaterialDisplayUrl(material, kind) ?? material.src,
    }))
  const videoMaterial =
    kind === 'pipe-video'
      ? primaryMaterial
      : (materials.find((material) => isVideoMaterial(material)) ?? null)
  const aspectMaterial = videoMaterial ?? primaryMaterial
  const primaryImageUrl = isImageMaterial(primaryMaterial)
    ? resolveImageMaterialDisplayUrl(primaryMaterial, kind)
    : null
  const previewVideoUrl = videoMaterial
    ? resolveVideoMaterialPlaybackUrl(videoMaterial, kind)
    : null
  const previewVideoCoverUrl = normalizeMediaUrl(videoMaterial?.cover)
  const coverUrl =
    kind === 'pipe-image'
      ? resolveFirstMediaUrl(
          primaryImageUrl,
          item.cover,
          item.bgmLib?.cover,
          item.relCartoons?.[0]?.avatar,
        )
      : resolveFirstMediaUrl(
          previewVideoCoverUrl,
          primaryMaterial?.cover,
          item.cover,
          primaryImageUrl,
          item.bgmLib?.cover,
          item.relCartoons?.[0]?.avatar,
        )

  return {
    kind,
    label: resolveLabel(kind),
    item,
    primaryMaterial,
    previewImages,
    previewVideoCoverUrl,
    previewVideoUrl,
    previewAudioUrl:
      normalizeMediaUrl(item.radio?.materialUrl) ?? normalizeMediaUrl(item.bgmLib?.audioUrl),
    coverUrl,
    aspectRatio: resolveAspectRatio(kind, aspectMaterial),
    hasCollection: Boolean(item.collectionInfo?.collectionId),
    hasAnchors: Boolean(item.anchors?.length),
  }
}
